// Toast wrapper that keeps at most one message visible AND dedups
// rapid-fire repeats of the same toast within a short window.
//
// react-toastify's <ToastContainer limit={1} /> caps the *visible*
// count but queues subsequent calls — when a fast-scan workflow fires
// errors back-to-back, the picker sees outdated reasons pop up after
// they've already moved past them. This helper dismisses the queue
// first so the newest call always wins outright.
//
// Duplicate-suppression: a few picker rejection paths fire the same
// toast from more than one call site (e.g. the local-first sync queue
// reports BE rejections via onSyncReject AND the legacy submitScan
// also surfaces them when the bundle hits a non-local-first warehouse
// in transition). Without dedup, the user sees the same "Tote not
// available" toast twice — once dismisses + shows toast A, the second
// dismisses + shows toast B with the same content. A short cooldown
// keyed on toastId-or-message catches these without affecting genuine
// distinct messages.
//
// Use it anywhere a scan workflow surfaces feedback (picking, drop,
// missing-item, book-in, etc.). Direct `toast.X()` calls still work
// — they just don't override the queue and don't participate in
// dedup.
import { toast } from "react-toastify";
import type { ToastOptions } from "react-toastify";

type ToastKind = "error" | "info" | "success" | "warn";

// Window during which a repeat toast with the same key is dropped.
// Long enough to suppress same-event re-fires from different code
// paths (typical gap: <100 ms) but short enough that a deliberate
// second user action a moment later still gets feedback.
const DEDUP_WINDOW_MS = 1500;
const lastShownAt = new Map<string, number>();

const dedupKey = (message: string, options?: ToastOptions): string => {
    const id = options?.toastId;
    if (typeof id === "string" && id.length > 0) return `id:${id}`;
    if (typeof id === "number") return `id:${id}`;
    return `msg:${message}`;
};

export const showToast = (
    kind: ToastKind,
    message: string,
    options?: ToastOptions,
) => {
    const key = dedupKey(message, options);
    const now = Date.now();
    const last = lastShownAt.get(key);
    if (last !== undefined && (now - last) < DEDUP_WINDOW_MS) {
        // Same toast just fired — drop the repeat so the picker doesn't
        // see the message flash twice. The original is still on screen
        // (or about to be, if the first call landed within the same
        // event loop tick).
        return null;
    }
    lastShownAt.set(key, now);
    toast.dismiss();
    return toast[kind](message, options);
};

export const dismissToasts = () => {
    lastShownAt.clear();
    toast.dismiss();
};
